import React, { useEffect, useState } from "react";

// region imports - Third party
import { UseFormReturn } from "react-hook-form";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { getValidationErrors } from "@npm-telluria-tecnologia/telluria-react-hooks";
// endregion imports - Third party
// region imports - Atoms
import Step from "@atoms/Step";
// endregion imports - Atoms
// region imports - Languages
import { ConcretingRequestMessages, GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion imports - Languages
// region imports - Libraries
import Yup from "@libraries/yup";
import { Fab } from "@libraries/mui";
import { SetStateAction } from "@libraries/jotai";
import { ArrowBack, ArrowForward, Check } from "@libraries/icons";
// endregion imports - Libraries
// region imports - Styles
import useTheme from "@styles/useTheme";
// endregion imports - Styles
import * as Styled from "./styles";

// region Interfaces
export interface IStepTabProps {
  id: string;
  tabLabel: string;
  schema?: Yup.AnyObjectSchema;
  invisible?: boolean;
}

interface IStepTabsProps {
  content: IStepTabProps[];
  selectedStepTab: number;
  mode: "CREATE" | "EDIT";
  disableControlButtons?: boolean;
  setSelectedStepTab: (update: SetStateAction<number>) => void;
  onChangeTab?: (indexNewTab: number) => Promise<{
    stepTabIsAvailable: boolean;
    indexNextAvailableStepTab: number;
    toastMessage: string;
  }>;
  onFinish: () => void;
}
// endregion Interfaces

// region Functions
/**
 * Validates if the clicked stepTab is available
 * @param steps process guides
 * @param methods individual functions to manage the form state.
 * @param stepTabIndexClicked stepTab index clicked
 * @returns Promise object indicates whether the stepTab is available,
 * the index of the next available stepTab for redirection if needed and the error message to be displayed
 * If the error is related to inactive/with pendencies contract, display error message accordingly
 */
export async function validateStepTabForm<T = any>(
  steps: IStepTabProps[],
  methods: UseFormReturn<any, any>,
  stepTabIndexClicked: number
) {

  let contractWithPendenciesError = false;
  let inactiveContractError = false;

  const promises = steps
    .filter((_, index) => index < stepTabIndexClicked)
    /** validates each stepTab to check for errors */
    .map(
      (step) => step.schema?.validate(methods.getValues(), { abortEarly: false }).then(
        () => true,
        (error) => {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);

            contractWithPendenciesError = error.value.contract.status === "PENDING";
            inactiveContractError = error.value.contract.status === "INACTIVE";

            Object.keys(errors).forEach((propName) => {
              methods.setError(propName as any, {
                message: errors[propName]
              });
            });
          }

          return false;
        }
      ) ?? true
    );

  const results = await Promise.all(promises);

  const indextNextStepTabWithError = results.findIndex((success) => !success);
  const thereIsAnInvalidForm = indextNextStepTabWithError >= 0;

  const indexNextAvailableStepTab = thereIsAnInvalidForm
    ? indextNextStepTabWithError
    : stepTabIndexClicked;

  return {
    stepTabIsAvailable: !thereIsAnInvalidForm,
    indexNextAvailableStepTab,
    toastMessage:
      contractWithPendenciesError
        ? ConcretingRequestMessages.contractWithPendenciesMessage.toString()
        : inactiveContractError
          ? ConcretingRequestMessages.inactiveContractMessage.toString()
          : GlobalMessages.invalidFormInputs.toString()
  };
}
// endregion Functions

const StepTabs: React.FC<IStepTabsProps> = ({
  content,
  children,
  selectedStepTab,
  setSelectedStepTab,
  onChangeTab,
  mode,
  disableControlButtons,
  onFinish
}) => {
  // region Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks

  // region States
  const [accessedTabs, setAccessedTabs] = useState<number[]>([0]);
  // endregion States

  // region Step tab handlers
  const changeTab = (indexNewTab: number) => {
    if (!onChangeTab || indexNewTab <= selectedStepTab) {
      setSelectedStepTab(indexNewTab);
      setAccessedTabs((value) => [...new Set([...value, indexNewTab])]);

      return;
    }

    onChangeTab(indexNewTab).then(
      ({ stepTabIsAvailable, indexNextAvailableStepTab, toastMessage }) => {
        if (!stepTabIsAvailable) {
          addToast({
            type: "info",
            title: t(GlobalMessages.warningText),
            description: t(toastMessage),
            overwrite: true
          });
        }

        setSelectedStepTab(indexNextAvailableStepTab);
      }
    );
  };

  const handleChange = (
    _: React.SyntheticEvent<Element, Event>,
    newValue: number
  ) => {
    changeTab(newValue);
  };

  const nextTab = () => {
    if (selectedStepTab === content.length - 1) {
      onFinish();

      return;
    }

    const nextAccessedTab = selectedStepTab + 1;

    changeTab(nextAccessedTab);
  };

  const previousTab = () => {
    if (selectedStepTab <= 0) return;

    setSelectedStepTab((value) => value - 1);
  };
  // endregion Step tab handlers

  // region Constants
  const lastStepTab = selectedStepTab === content.length - 1;
  // endregion Constants

  // region useEffects
  useEffect(() => {
    setAccessedTabs((value) => [...new Set([...value, selectedStepTab])]);
  }, [selectedStepTab]);
  // endregion useEffects

  return (
    <>
      <Styled.StepsContainer
        value={selectedStepTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="scrollable force tabs"
      >
        {content.map(({ id, tabLabel }, index) => {
          const indexTab = accessedTabs.findIndex((value) => value === index);
          const accessedTab = indexTab >= 0;
          const disabled = mode === "CREATE" && !accessedTab;

          return (
            <Step
              key={`tab_${id}`}
              label={t(tabLabel)}
              disabled={disabled}
              /** as this component is from the mui library, it is not possible to pass custom style props */
              style={{
                backgroundColor: disabled ? theme.colors.disabled : theme.colors.secondary
              }}
            />
          );
        })}
      </Styled.StepsContainer>
      {children}
      {!disableControlButtons && (
        <Styled.ControlButtons>
          {selectedStepTab > 0 && (
            <Fab color="primary" size="medium" onClick={() => previousTab()}>
              <ArrowBack fontSize="medium" />
            </Fab>
          )}
          <Fab
            color={lastStepTab ? "success" : "primary"}
            size="large"
            onClick={() => nextTab()}
          >
            {lastStepTab ? (
              <Check fontSize="large" />
            ) : (
              <ArrowForward fontSize="large" />
            )}
          </Fab>
        </Styled.ControlButtons>
      )}
    </>
  );
};

StepTabs.defaultProps = {
  onChangeTab: undefined,
  disableControlButtons: false
};

export default StepTabs;
